// CreateModal.tsx
import React, { useEffect, useRef, useState } from "react";
import { Modal, Button, Form, message } from "antd";
import { useTranslation } from "react-i18next";
import { AnyObject } from "yup";
import UserCreateForm from "./Form";
import {
  UserUpsetParams,
  UserMembership,
  useUserCreate,
} from "../../../../hooks/api/users";
import { useNavigate } from "react-router";
import { useMessageError } from "../../../../hooks/common";

interface UserInitialState {
  email?: string;
  fullName?: string;
  address?: string;
  phone?: string;
  photoUrl?: string;
  subscriptionStatus?: string;
  memberships?: UserMembership[];
}

interface CreateModalModalProps {
  open: boolean;
  mode: "create" | "edit";
  onClose: () => void;
  setTableKey?: () => void;
  initialState?: UserInitialState;
  vaadId?: string;
}

export default function CreateModal({
  open,
  mode,
  onClose,
  setTableKey,
  initialState,
  vaadId,
}: CreateModalModalProps) {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const initialValuesRef = useRef<AnyObject | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const userCreate = useUserCreate();
  const isDraftDisabled = mode === "edit" && !isDirty;

  const handleSubmit = async (values: any) => {
    const payload: UserUpsetParams = {
      email: values.email,
      fullName: values.fullName,
      address: values.address,
      phone: values.phone,
      role: "student",
      memberships: [
        {
          vaad: {
            id: vaadId || values.vaadId,
          },
          // discountValue: 10,
          // discountEndDate: {},
        },
      ],
    };

    try {
      await userCreate.fetch(payload).then((res) => {
        if (res?.email) {
          message.success("User has been created successfully");
          form.resetFields();
          onClose();
          // navigate(`/users/`);
          setTableKey?.();
        }
      });
    } catch (error: any) {
      message.error(error?.message);
    }
  };

  useMessageError([userCreate]);

  const handleClose = () => {
    form.resetFields();
    initialValuesRef.current = null;
    setIsDirty(false);
    onClose();
  };

  useEffect(() => {
    if (!open) return;

    if (mode === "edit" && initialState) {
      const formValues = {
        ...initialState,
        memberships: initialState.memberships?.[0]
          ? [
            {
              vaad: { id: initialState.memberships[0].vaad?.id },
              discountValue: initialState.memberships[0].discountValue,
            },
          ]
          : [{ vaad: { id: undefined }, discountValue: 0 }],
      };

      form.setFieldsValue(formValues);
      initialValuesRef.current = formValues;
      setIsDirty(false);
    }

    if (mode === "create") {
      initialValuesRef.current = null;
      setIsDirty(true);
      if (vaadId) {
        form.setFieldsValue({ vaadId });
      }
    }
  }, [open, mode, initialState]);

  const footer = [
    <Button key="cancel" onClick={handleClose}>
      {t("common.cancel")}
    </Button>,
    <Button
      key="send"
      type="primary"
      loading={userCreate.loading}
      onClick={() => form.submit()}
    >
      {mode === "edit" ? "Save Changes" : "Add User"}
    </Button>,
  ];

  return (
    <Modal
      title={mode === "edit" ? "Edit User" : "Add User"}
      open={open}
      onCancel={handleClose}
      destroyOnHidden
      width={520}
      footer={footer}
    >
      <Form
        form={form}
        layout="vertical"
        name="userForm"
        onFinish={handleSubmit}
        onValuesChange={(_, allValues) => {
          if (mode !== "edit") return;

          const isChanged =
            JSON.stringify(allValues) !==
            JSON.stringify(initialValuesRef.current);

          setIsDirty(isChanged);
        }}
      >
        <UserCreateForm hideVaad={!!vaadId} vaadId={vaadId} />
      </Form>
    </Modal>
  );
}
